import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/** Download formats offered by the data exports. The value doubles as the file extension. */
export type ExportFormat = 'xlsx' | 'csv';

/** Options for the download Format dropdown (see DownloadFormatField). */
export const EXPORT_FORMAT_OPTIONS: Array<{ value: ExportFormat; label: string }> = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV' },
];

export interface ExportSheet {
    /** Worksheet name (Excel tab label; also the suffix of a secondary CSV file's name). */
    sheetName: string;
    /** Column header row. */
    headers: string[];
    /** Data rows, one array of cell values per row. */
    rows: unknown[][];
}

interface ExportSheetsToFileOptions {
    /** The first sheet is the primary dataset: when it has no rows, nothing is downloaded. */
    sheets: ExportSheet[];
    /** Base filename without extension; the extension is appended from `format`. */
    fileName: string;
    format: ExportFormat;
}

interface ExportRowsToFileOptions extends ExportSheet {
    fileName: string;
    format: ExportFormat;
}

function addSheet(workbook: ExcelJS.Workbook, { sheetName, headers, rows }: ExportSheet): void {
    const worksheet = workbook.addWorksheet(sheetName);
    worksheet.columns = headers.map((header) => ({ header, width: 20 }));
    rows.forEach((row) => {
        worksheet.addRow(row);
    });
    worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
    });
}

/** "Pipelines" → "pipelines": the filename suffix for a secondary sheet's CSV file. */
function sheetFileSuffix(sheetName: string): string {
    return sheetName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * Builds an ExcelJS workbook (bold header rows, width-20 columns) from one or more sheets,
 * writes it in the requested format and triggers the download(s) via file-saver.
 *
 * XLSX keeps every populated sheet in one workbook. CSV cannot hold multiple sheets, so the
 * first sheet downloads as `<fileName>.csv` and each further populated sheet as its own
 * `<fileName>-<sheet>.csv` — the full dataset is preserved in both formats.
 *
 * Returns false without downloading anything when the first (primary) sheet has no rows —
 * the caller surfaces a "no data" message; returns true when a file was downloaded.
 */
export async function exportSheetsToFile({ sheets, fileName, format }: ExportSheetsToFileOptions): Promise<boolean> {
    if (sheets.length === 0 || sheets[0].rows.length === 0) return false;

    const populated = sheets.filter((sheet) => sheet.rows.length > 0);

    if (format === 'xlsx') {
        const workbook = new ExcelJS.Workbook();
        populated.forEach((sheet) => {
            addSheet(workbook, sheet);
        });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${fileName}.xlsx`);
        return true;
    }

    for (const [index, sheet] of populated.entries()) {
        const workbook = new ExcelJS.Workbook();
        addSheet(workbook, sheet);
        const buffer = await workbook.csv.writeBuffer();
        const name = index === 0 ? fileName : `${fileName}-${sheetFileSuffix(sheet.sheetName)}`;
        saveAs(new Blob([buffer]), `${name}.csv`);
    }
    return true;
}

/**
 * Single-sheet convenience wrapper over {@link exportSheetsToFile}. Shared by the POI and
 * high-risk air-valve exports so both produce identically-structured files.
 */
export async function exportRowsToFile({
    sheetName,
    headers,
    rows,
    fileName,
    format,
}: ExportRowsToFileOptions): Promise<boolean> {
    return exportSheetsToFile({ sheets: [{ sheetName, headers, rows }], fileName, format });
}
